import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, Plus, Car, User, DollarSign, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  draft:    "bg-slate-500/15 text-slate-300 border-slate-500/30",
  sent:     "bg-blue-500/15 text-blue-300 border-blue-500/30",
  accepted: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  closed:   "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  lost:     "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

export default function Quotes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/quotes" + (statusFilter ? `?status=${statusFilter}` : ""));
      setRows(r.data || []);
    } catch (e) { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const remove = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="quotes-page">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-600/15 border border-purple-500/20 flex items-center justify-center">
            <FileText className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">Sales</div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Orçamentos</h1>
            <p className="text-sm text-slate-400 mt-1">VIN-based quotes for dealerships and end customers.</p>
          </div>
        </div>
        <Link to="/quotes/new">
          <Button data-testid="new-quote-btn" className="bg-purple-600 hover:bg-purple-500">
            <Plus className="w-4 h-4 mr-2" /> Novo Orçamento
          </Button>
        </Link>
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-2 border-b border-slate-800 pb-1">
        {[
          { k: "", label: "All" },
          { k: "draft", label: "Draft" },
          { k: "sent", label: "Sent" },
          { k: "accepted", label: "Accepted" },
          { k: "closed", label: "Closed" },
          { k: "lost", label: "Lost" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`filter-${t.k || "all"}`}
            onClick={() => setStatusFilter(t.k)}
            className={`px-3 py-1.5 text-xs uppercase tracking-[0.14em] font-bold transition ${statusFilter === t.k ? "text-purple-300 border-b-2 border-purple-400 -mb-[1px]" : "text-slate-500 hover:text-slate-300"}`}
          >{t.label}</button>
        ))}
      </div>

      {/* List */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-900/50 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">
              <th className="text-left px-4 py-3">Quote #</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Vehicle</th>
              <th className="text-left px-4 py-3">Salesperson</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center text-slate-500 py-10">Loading...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-500 py-10" data-testid="no-quotes">Nenhum orçamento ainda. Clique &quot;Novo Orçamento&quot; para começar.</td></tr>
            )}
            {rows.map((q) => (
              <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-t border-slate-800/60 hover:bg-slate-900/30 transition">
                <td className="px-4 py-2.5">
                  <Link to={`/quotes/${q.id}`} className="text-sm font-mono text-purple-300 hover:text-purple-200">{q.quote_number}</Link>
                </td>
                <td className="px-4 py-2.5 text-sm text-slate-200">
                  <div className="flex items-center gap-1.5"><User className="w-3 h-3 text-slate-500" />{q.customer_name}</div>
                  {q.customer_phone && <div className="text-[11px] text-slate-500 mt-0.5">{q.customer_phone}</div>}
                </td>
                <td className="px-4 py-2.5 text-sm text-slate-300">
                  <div className="flex items-center gap-1.5"><Car className="w-3 h-3 text-slate-500" />{[q.vehicle_year, q.vehicle_make, q.vehicle_model].filter(Boolean).join(" ") || "-"}</div>
                  {q.vin && <div className="text-[10px] text-slate-500 font-mono mt-0.5">VIN: {q.vin}</div>}
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-400">{q.salesperson_name || <span className="text-slate-600">-</span>}</td>
                <td className="px-4 py-2.5 text-right font-mono text-emerald-300">${(q.total || 0).toFixed(2)}</td>
                <td className="px-4 py-2.5 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border ${STATUS_COLORS[q.status] || STATUS_COLORS.draft}`}>{q.status}</span>
                </td>
                <td className="px-2 py-2.5">
                  <div className="flex items-center gap-1">
                    <Link to={`/quotes/${q.id}`} data-testid={`quote-open-${q.id}`} className="text-slate-500 hover:text-blue-400 p-1"><ExternalLink className="w-4 h-4" /></Link>
                    {q.status !== "closed" && (
                      <button data-testid={`quote-delete-${q.id}`} onClick={() => remove(q.id)} className="text-slate-500 hover:text-rose-400 p-1"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
